import { getLang } from '../i18n/lang';
import { dataLabel } from '../i18n/data';
import type { Announcement, FilterState, ChartDataResponse, PaginatedResponse, TableSort, MapPoint, WahlkreiseGeoJson, MetaData, NewsStats, Spotlight, UserSubmissionBody } from '../types';

// Module-level config; populated via setApiBase() before render() mounts the widget.
// Default '/api' lets the dev server proxy continue to work in vite's dev mode.
const config = { apiBase: '/api' };

export function setApiBase(base: string): void {
  config.apiBase = base.replace(/\/+$/, '');
}

/** The configured base, for callers that need to name an endpoint in output
    rather than call it (the Dataset markup's distribution URL). */
export function getApiBase(): string {
  return config.apiBase;
}

// Fire a one-shot, fire-and-forget page-view beacon so widget loads can be
// counted exactly (Railway egress only approximates it). sendBeacon survives a
// mid-mount navigation; keepalive fetch is the fallback. Widget name rides in
// the query string (no JSON body → no CORS preflight); server answers 204.
export function trackPageview(): void {
  const url = `${config.apiBase}/analytics/pageview?w=investments&lang=${getLang()}`;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      navigator.sendBeacon(url);
      return;
    }
    void fetch(url, { method: 'POST', keepalive: true }).catch(() => {});
  } catch {
    // Analytics must never affect the widget.
  }
}

/** English widget: show the English headline the API ships alongside. */
function localiseHeadlines(rows: Announcement[]): Announcement[] {
  if (getLang() !== 'en') return rows;
  return rows.map(a => (a.headline_en ? { ...a, headline: a.headline_en } : a));
}

function buildParams(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.federalStates.length) params.set('federalStates', filters.federalStates.join('|'));
  if (filters.investmentTypes.length) params.set('investmentTypes', filters.investmentTypes.join('|'));
  if (filters.investmentStatuses.length) params.set('investmentStatuses', filters.investmentStatuses.join('|'));
  if (filters.industries.length) params.set('industries', filters.industries.join('|'));
  if (filters.companies.length) params.set('companies', filters.companies.join('|'));
  if (filters.search) params.set('search', filters.search);
  if (filters.spotlight) params.set('spotlight', filters.spotlight);
  return params;
}

// Spotlight definitions change rarely — cache the promise for the page's
// lifetime (same pattern as the admin's lookup fetchers).
let spotlightsCache: Promise<Spotlight[]> | null = null;

export function fetchSpotlights(): Promise<Spotlight[]> {
  if (!spotlightsCache) {
    spotlightsCache = fetch(`${config.apiBase}/spotlights`)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch spotlights');
        return res.json() as Promise<Spotlight[]>;
      })
      .catch(err => {
        spotlightsCache = null; // allow retry on next call
        throw err;
      });
  }
  // Texts in the widget language (i18n.translations, domain investments.spotlight),
  // German where none exist yet. Applied per call, not cached, so a live
  // language switch needs no refetch.
  return spotlightsCache.then(rows => rows.map(s => ({
    ...s,
    label: dataLabel('investments.spotlight', s.label),
    title: dataLabel('investments.spotlight', s.title),
    description: s.description ? dataLabel('investments.spotlight', s.description) : s.description,
  })));
}

// Latest-5 news banner ("Aktuelle Meldungen") — follows the active filters
// plus the chart-side narrowings (industry drill via industriesNarrow,
// country/Wahlkreis selection via clusterIds), so no client-side caching;
// the server micro-cache covers repeat combos.
export async function fetchLatestAnnouncements(
  filters: FilterState,
  signal?: AbortSignal,
  clusterIds?: number[],
  industriesNarrow?: string[],
): Promise<Announcement[]> {
  const params = buildParams(filters);
  if (clusterIds?.length) params.set('clusterIds', clusterIds.join('|'));
  if (industriesNarrow?.length) params.set('industriesNarrow', industriesNarrow.join('|'));
  const qs = params.toString();
  const res = await fetch(`${config.apiBase}/announcements/latest${qs ? `?${qs}` : ''}`, { signal });
  if (!res.ok) throw new Error('Failed to fetch latest announcements');
  return localiseHeadlines(await res.json());
}

export async function fetchMeta(): Promise<MetaData> {
  const res = await fetch(`${config.apiBase}/meta`);
  if (!res.ok) throw new Error('Failed to fetch meta');
  return res.json();
}

export async function fetchClusterAnnouncements(clusterId: number): Promise<Announcement[]> {
  const res = await fetch(`${config.apiBase}/announcements/cluster/${clusterId}`);
  if (!res.ok) throw new Error('Failed to fetch cluster announcements');
  return localiseHeadlines(await res.json());
}

export async function fetchWahlkreise(): Promise<WahlkreiseGeoJson> {
  const res = await fetch(`${config.apiBase}/wahlkreise`);
  if (!res.ok) throw new Error('Failed to fetch wahlkreise');
  return res.json();
}

export async function fetchChartData(
  filters: FilterState,
  signal?: AbortSignal,
  industryDrill?: { level: 1 | 2; label: string },
  chartClusterIds?: number[],
  industriesNarrow?: string[],
): Promise<ChartDataResponse & { points: MapPoint[]; maxVolume: number }> {
  const params = buildParams(filters);
  if (industryDrill) {
    params.set('industryDrillLevel', String(industryDrill.level));
    params.set('industryDrillLabel', industryDrill.label);
  }
  if (chartClusterIds?.length) {
    params.set('chartClusterIds', chartClusterIds.join('|'));
  }
  if (industriesNarrow?.length) {
    params.set('industriesNarrow', industriesNarrow.join('|'));
  }
  const res = await fetch(`${config.apiBase}/chart-data?${params}`, { signal });
  if (!res.ok) throw new Error('Failed to fetch chart data');
  const data: ChartDataResponse = await res.json();

  const { dictionaries: d, points: rows, maxVolume } = data.mapPoints;
  const points: MapPoint[] = rows.map(r => ({
    announcement_id: r[0] as number,
    location_id: r[1] as number,
    cluster_id: r[2] as number | null,
    latitude: r[3] as number,
    longitude: r[4] as number,
    type_of_investment: r[5] != null ? d.types[r[5] as number]! : null,
    investment_volume_value_eur: r[6] as number | null,
    investing_company: r[7] != null ? d.companies[r[7] as number]! : null,
    city_name: d.cities[r[8] as number]!,
    federal_state: r[9] != null ? d.states[r[9] as number]! : null,
    project_name: r[10] != null ? d.projects[r[10] as number]! : null,
    wahlkreis_nr: r[11] as number | null,
    primary_industry: r[12] != null ? d.industries[r[12] as number]! : null,
    industry_l2: r[13] != null ? d.industriesL2[r[13] as number]! : null,
    industry_l3: r[14] != null ? d.industriesL3[r[14] as number]! : null,
  }));

  return { ...data, points, maxVolume };
}

export async function fetchAnnouncementsPage(
  filters: FilterState,
  page: number,
  pageSize: number,
  sort: TableSort,
  findCluster?: number,
  clusterIds?: number[],
  signal?: AbortSignal,
  industriesNarrow?: string[],
): Promise<PaginatedResponse> {
  const params = buildParams(filters);
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  params.set('sort', sort.field);
  params.set('dir', sort.dir);
  if (findCluster != null) params.set('findCluster', String(findCluster));
  if (clusterIds?.length) params.set('clusterIds', clusterIds.join('|'));
  if (industriesNarrow?.length) params.set('industriesNarrow', industriesNarrow.join('|'));
  const res = await fetch(`${config.apiBase}/announcements?${params}`, { signal });
  if (!res.ok) throw new Error('Failed to fetch announcements page');
  return res.json();
}

// Unpaginated fetch for the CSV export: without page/pageSize the endpoint
// returns every matching announcement (latest per cluster, like the table)
// with its locations in one response.
export async function fetchAnnouncementsExport(
  filters: FilterState,
  clusterIds?: number[],
  industriesNarrow?: string[],
): Promise<Announcement[]> {
  const params = buildParams(filters);
  if (clusterIds?.length) params.set('clusterIds', clusterIds.join('|'));
  if (industriesNarrow?.length) params.set('industriesNarrow', industriesNarrow.join('|'));
  const res = await fetch(`${config.apiBase}/announcements?${params}`);
  if (!res.ok) throw new Error('Failed to fetch export data');
  return res.json();
}

// City autocomplete for the suggestion form. Served by our own API (which
// proxies + caches Photon) so third-party embed pages' CSP can't block it.
export interface CityHit {
  city_name: string;
  federal_state: string | null;
  latitude: number;
  longitude: number;
  postcode: string | null;
  place_type: string | null;
}

export async function searchCities(q: string, signal?: AbortSignal): Promise<CityHit[]> {
  const res = await fetch(`${config.apiBase}/cities?q=${encodeURIComponent(q)}`, { signal });
  if (!res.ok) throw new Error('City lookup unavailable');
  return res.json();
}

// Anti-bot challenge for the feedback forms: fetched when the dialog opens,
// echoed on submit. The server enforces a minimum token age, so no client-side
// caching — every dialog open gets a fresh token.
export async function fetchSubmissionChallenge(): Promise<string> {
  const res = await fetch(`${config.apiBase}/user-submissions/challenge`);
  if (!res.ok) throw new Error('Failed to fetch submission challenge');
  const data = (await res.json()) as { token: string };
  return data.token;
}

// Submit user feedback (correction or new-project suggestion). Deliberately
// no retry: a retry on a flaky connection could double-submit.
export async function submitFeedback(body: UserSubmissionBody): Promise<void> {
  const res = await fetch(`${config.apiBase}/user-submissions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error('Failed to submit feedback');
}

export async function fetchNewsStats(): Promise<NewsStats> {
  const res = await fetch(`${config.apiBase}/news/stats`);
  if (!res.ok) throw new Error('Failed to fetch news stats');
  return res.json();
}
